import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";

export default function LicensingGuide() {
  return (
    <div className="min-h-screen bg-background py-10">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <Link href="/">
            <Button variant="ghost" className="flex items-center gap-1 hover:bg-transparent hover:text-primary p-0">
              <ChevronLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Button>
          </Link>
        </div>
        
        <div className="max-w-3xl mx-auto bg-card p-8 rounded-lg shadow-sm">
          <h1 className="text-3xl font-bold mb-6">Licensing Guide</h1>
          
          <div className="prose dark:prose-invert max-w-none">
            <p className="mb-4">
              This guide explains how to license music from NoLicense Nearby and answers common questions about our licensing process.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">Understanding Our Licenses</h2>
            <p className="mb-4">
              We offer music licenses based on how and where you'll use the music. Each license grants you specific rights for a defined time period.
            </p>
            
            <div className="mt-6 mb-8">
              <h3 className="text-lg font-semibold mb-3">License Types</h3>
              
              <div className="grid gap-4">
                <div className="border border-border rounded-lg p-4">
                  <h4 className="font-semibold">Basic Creator License</h4>
                  <p className="text-sm mb-2">Perfect for small creators and YouTubers up to 100K subscribers</p>
                  <ul className="list-disc pl-6 text-sm">
                    <li>YouTube videos (gaming, vlogs, tutorials)</li>
                    <li>Instagram/TikTok content</li>
                    <li>Personal podcast intro/outro</li>
                    <li>Student films</li>
                    <li>Personal website background music</li>
                    <li>Non-monetized content</li>
                    <li>Single platform use only</li>
                    <li>12-month license duration</li>
                    <li>Starting at $49 per track</li>
                    <li>5-track bundle: $199 (save $46)</li>
                    <li>10-track bundle: $349 (save $141)</li>
                  </ul>
                </div>
                
                <div className="border border-border rounded-lg p-4">
                  <h4 className="font-semibold">Professional License</h4>
                  <p className="text-sm mb-2">For professional content creators up to 1M subscribers</p>
                  <ul className="list-disc pl-6 text-sm">
                    <li>All Basic License features</li>
                    <li>Monetized content across platforms</li>
                    <li>Online courses and educational content</li>
                    <li>Small business advertisements</li>
                    <li>Local TV/Radio commercials</li>
                    <li>Mobile apps and games</li>
                    <li>Corporate presentations</li>
                    <li>Wedding/Event videos</li>
                    <li>Perpetual license</li>
                    <li>Starting at $199 per track</li>
                    <li>5-track bundle: $799 (save $196)</li>
                    <li>10-track bundle: $1,499 (save $491)</li>
                    <li>Add-ons available: Extended Territory Rights ($149), Broadcast Rights ($299)</li>
                  </ul>
                </div>
                
                <div className="border border-border rounded-lg p-4">
                  <h4 className="font-semibold">Enterprise License</h4>
                  <p className="text-sm mb-2">For businesses and large channels with unlimited audience</p>
                  <ul className="list-disc pl-6 text-sm">
                    <li>All Professional License features</li>
                    <li>National/International TV commercials</li>
                    <li>Major brand campaigns</li>
                    <li>Broadcast network shows</li>
                    <li>Feature films</li>
                    <li>Major video game releases</li>
                    <li>National radio campaigns</li>
                    <li>Multi-territory distribution</li>
                    <li>White-label usage</li>
                    <li>Perpetual license</li>
                    <li>Starting at $499 per track</li>
                    <li>5-track bundle: $1,999 (save $496)</li>
                    <li>10-track bundle: $3,999 (save $991)</li>
                    <li>Add-ons available: Extended Territory Rights ($149), Broadcast Rights ($299)</li>
                  </ul>
                </div>
                
                <div className="border border-border rounded-lg p-4">
                  <h4 className="font-semibold">Custom License</h4>
                  <p className="text-sm mb-2">For special projects with unique requirements</p>
                  <ul className="list-disc pl-6 text-sm">
                    <li>Tailored to your specific needs</li>
                    <li>Custom territory and usage rights</li>
                    <li>Custom track edits and modifications</li>
                    <li>Flexible licensing terms</li>
                    <li>Contact us for pricing</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">Available Add-Ons</h2>
            <div className="grid gap-4 mb-6">
              <div className="border border-border rounded-lg p-4">
                <h4 className="font-semibold">Extended Territory Rights</h4>
                <p className="text-sm mb-2">Expand your license to cover additional territories</p>
                <ul className="list-disc pl-6 text-sm">
                  <li>Professional License: +$149</li>
                  <li>Enterprise License: +$149</li>
                  <li>Basic License: +$299</li>
                </ul>
              </div>
              
              <div className="border border-border rounded-lg p-4">
                <h4 className="font-semibold">Broadcast Rights</h4>
                <p className="text-sm mb-2">Add TV, radio, and streaming platform rights</p>
                <ul className="list-disc pl-6 text-sm">
                  <li>Professional License: +$299</li>
                  <li>Enterprise License: +$299</li>
                </ul>
              </div>
              
              <div className="border border-border rounded-lg p-4">
                <h4 className="font-semibold">Custom Edit Service</h4>
                <p className="text-sm mb-2">Get your track professionally edited to custom specifications</p>
                <ul className="list-disc pl-6 text-sm">
                  <li>All license tiers: +$149</li>
                </ul>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">Licensing Process</h2>
            <ol className="list-decimal pl-6 mb-6">
              <li className="mb-2">
                <strong>Browse our catalog</strong> and find tracks that fit your project
              </li>
              <li className="mb-2">
                <strong>Select a license tier</strong> that matches your usage requirements
              </li>
              <li className="mb-2">
                <strong>Choose quantity and add-ons</strong> to customize your license
              </li>
              <li className="mb-2">
                <strong>Complete payment</strong> via our secure checkout powered by Stripe
              </li>
              <li className="mb-2">
                <strong>Download your licensed music</strong> in high-quality MP3 and WAV formats
              </li>
            </ol>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">File Formats</h2>
            <p className="mb-4">
              We provide all licensed tracks in the following formats:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>MP3:</strong> 320 kbps, high-quality compressed format</li>
              <li><strong>WAV:</strong> Uncompressed 44.1kHz/16-bit for professional use</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">Copyright Protection</h2>
            <p className="mb-4">
              All tracks on NoLicense Nearby are 100% original and created by NoBanks Nearby. When you license a track, you can use it in your content without worry about copyright claims or takedowns, as long as you adhere to the terms of your license.
            </p>
            
            <h2 className="text-xl font-semibold mt-6 mb-3">Ready to License?</h2>
            <p className="mb-4">
              Start browsing our catalog or submit a license request now.
            </p>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <Link href="/catalog">
                <Button>Browse Catalog</Button>
              </Link>
              <Link href="/license">
                <Button variant="outline">Submit License Request</Button>
              </Link>
            </div>
            
            <p className="mt-8 text-sm text-muted-foreground">
              Last Updated: April 4, 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}